import { useState, useMemo } from "react";
import styles from "./SupplierAPICredentials.module.css";
import { formatDateTime } from "../../../utils/dateHelpers";
import ApiCrendtialsDetail from "../../../components/Suppliers/ApiCrendtialsDetail";
import AddCredentialSetModal from "../../../components/Suppliers/AddCredentialsSetModal";
import { useCredentialsList } from "../../../api/suppliers/useCredentialsApi";
import { useSuppliersList } from "../../../api/suppliers/useSuppliersApi";

const mapApiCredentialToUi = (c) => ({
  id: c.credentialSetId || String(c.id),
  supplier: c.supplierName || "",
  environment: c.environment || "",
  authType: c.authenticationType || "",
  status: c.status || "",
  updatedAt: c.updatedAt || "",
  _raw: c,
});

const SupplierAPICredentials = () => {
  const { data: credentialsRaw, loading, error, refetch } = useCredentialsList();
  const { data: suppliers } = useSuppliersList({});
  const credentials = useMemo(
    () => (Array.isArray(credentialsRaw) ? credentialsRaw.map(mapApiCredentialToUi) : []),
    [credentialsRaw]
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const filteredCredentials = credentials.filter(cred => 
    cred.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cred.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddCredential = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCredentialCreated = () => {
    refetch();
    handleCloseAddModal();
  };

  const handleView = (cred) => {
    setSelectedCredential(cred);
    setIsDrawerOpen(true);
  };

  const handleEdit = (cred) => {
    console.log("Edit credential:", cred);
  };

  const handleToggleStatus = (cred) => {
    console.log("Toggle status for:", cred);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedCredential(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Supplier API Credentials</h1>
          <p className={styles.subtitle}>Manage API authentication per supplier</p>
        </div>
        
        <button className={styles.addBtn} onClick={handleAddCredential}>
          + Add Credential Set
        </button>
      </div>

      <div className={styles.alert}>
        <div className={styles.alertIcon}>🛡️</div>
        <div className={styles.alertContent}>
          <div className={styles.alertTitle}>Restricted Access</div>
          <div className={styles.alertText}>
            Access to API credentials is restricted to Admin and TechOps roles. All access is logged and audited.
          </div>
        </div>
      </div>

      {error && (
        <div className={styles.alert} style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
          <div className={styles.alertIcon}>⚠️</div>
          <div className={styles.alertContent}>
            <div className={styles.alertText}>{error}</div>
          </div>
        </div>
      )}

      <div className={styles.searchContainer}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search by supplier or credential ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Credential Set ID</th>
              <th>Supplier</th>
              <th>Environment</th>
              <th>Auth Type</th>
              <th>Status</th>
              <th>Updated At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "24px" }}>
                  Loading credentials...
                </td>
              </tr>
            ) : (
            filteredCredentials.map((cred) => (
              <tr key={cred.id}>
                <td className={styles.credId}>{cred.id}</td>
                <td className={styles.supplier}>{cred.supplier}</td>
                <td>
                  <span className={`${styles.badge} ${styles[`env${cred.environment}`]}`}>
                    {cred.environment}
                  </span>
                </td>
                <td>{cred.authType}</td>
                <td>
                  <span className={`${styles.badge} ${styles[`status${cred.status}`]}`}>
                    {cred.status}
                  </span>
                </td>
                <td className={styles.updatedAt}>{cred.updatedAt ? formatDateTime(cred.updatedAt) : "—"}</td>
                <td>
                  <div className={styles.actions}>
                    <button 
                      className={styles.actionBtn}
                      onClick={() => handleView(cred)}
                      title="View"
                    >
                      👁
                    </button>
                  </div>
                </td>
              </tr>
            ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Credential Set Modal */}
      <AddCredentialSetModal
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        onSubmit={handleCredentialCreated}
        suppliers={Array.isArray(suppliers) ? suppliers : []}
      />

      {/* View Credential Drawer */}
      <ApiCrendtialsDetail
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        credential={selectedCredential}
      />
    </div>
  );
};

export default SupplierAPICredentials;